"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import WaveDropsScrollingOverlay from "@/components/waves/drops/WaveDropsScrollingOverlay";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { isWaveDirectMessage } from "@/helpers/waves/wave.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useWaveBoostedDrops } from "@/hooks/useWaveBoostedDrops";
import { useWaveIsTyping } from "@/hooks/useWaveIsTyping";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TweetPreviewModeProvider, type TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { useWaveDropsClipboard } from "./hooks/useWaveDropsClipboard";
import { useDeferredNewestDrops } from "./hooks/useDeferredNewestDrops";
import { useWaveDropsNotificationRead } from "./hooks/useWaveDropsNotificationRead";
import { useWaveDropsSerialScroll } from "./hooks/useWaveDropsSerialScroll";
import { WaveDropsContent } from "./subcomponents/WaveDropsContent";
const EMPTY_DROPS: Drop[] = [];
interface WaveDropsAllProps {
  readonly waveId: string;
  readonly dropId: string | null;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly initialDrop: number | null;
  readonly dividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly bottomPaddingClassName?: string | undefined;
  readonly isMuted?: boolean | undefined;
}
const WaveDropsAllInner: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(86);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    connectedProfile
  } = useAuth();
  const {
    isAppleMobile
  } = useDeviceInfo();
  const containerRef = useRef(null);
  const {
    waveMessages,
    fetchNextPage,
    waitAndRevealDrop
  } = useVirtualizedWaveDrops(waveId, dropId);
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const typingMessage = useWaveIsTyping(waveId, connectedProfile?.handle ?? null, isMuted);
  let t2;
  if ($[0] !== waveId) {
    t2 = {
      waveId
    };
    $[0] = waveId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: boostedDrops
  } = useWaveBoostedDrops(t2);
  const scrollBehavior = useScrollBehavior();
  const {
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    isAtBottom,
    shouldPinToBottom,
    scrollToVisualBottom,
    forcePinToBottom
  } = scrollBehavior;
  const t3 = Boolean(connectedProfile?.handle);
  let t4;
  if ($[2] !== removeWaveDeliveredNotifications || $[3] !== t3 || $[4] !== waveId) {
    t4 = {
      waveId,
      enabled: t3,
      removeWaveDeliveredNotifications
    };
    $[2] = removeWaveDeliveredNotifications;
    $[3] = t3;
    $[4] = waveId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useWaveDropsNotificationRead(t4);
  waveMessages?.drops;
  const dropsForClipboard = waveMessages?.drops ?? EMPTY_DROPS;
  let t5;
  if ($[6] !== dropsForClipboard) {
    t5 = {
      containerRef,
      drops: dropsForClipboard
    };
    $[6] = dropsForClipboard;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useWaveDropsClipboard(t5);
  const initializedWaveRef = useRef(null);
  let t6;
  let t7;
  if ($[8] !== dividerSerialNo || $[9] !== setUnreadDividerSerialNo || $[10] !== waveId) {
    t6 = () => {
      if (initializedWaveRef.current === waveId) {
        return;
      }
      initializedWaveRef.current = waveId;
      setUnreadDividerSerialNo(dividerSerialNo ?? null);
    };
    t7 = [waveId, dividerSerialNo, setUnreadDividerSerialNo];
    $[8] = dividerSerialNo;
    $[9] = setUnreadDividerSerialNo;
    $[10] = waveId;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== isAppleMobile || $[14] !== shouldPinToBottom || $[15] !== waveId || $[16] !== waveMessages) {
    t8 = {
      waveId,
      isAppleMobile,
      waveMessages,
      shouldPinToBottom
    };
    $[13] = isAppleMobile;
    $[14] = shouldPinToBottom;
    $[15] = waveId;
    $[16] = waveMessages;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const {
    renderedWaveMessages,
    pendingDropsCount,
    revealPendingDrops: revealDeferredPendingDrops
  } = useDeferredNewestDrops(t8);
  let t9;
  if ($[18] !== dropId || $[19] !== fetchNextPage || $[20] !== initialDrop || $[21] !== renderedWaveMessages || $[22] !== scrollContainerRef || $[23] !== scrollToVisualBottom || $[24] !== shouldPinToBottom || $[25] !== waitAndRevealDrop || $[26] !== waveId || $[27] !== waveMessages) {
    t9 = {
      waveId,
      dropId,
      initialDrop,
      waveMessages,
      renderedWaveMessages,
      fetchNextPage,
      waitAndRevealDrop,
      scrollContainerRef,
      shouldPinToBottom,
      scrollToVisualBottom
    };
    $[18] = dropId;
    $[19] = fetchNextPage;
    $[20] = initialDrop;
    $[21] = renderedWaveMessages;
    $[22] = scrollContainerRef;
    $[23] = scrollToVisualBottom;
    $[24] = shouldPinToBottom;
    $[25] = waitAndRevealDrop;
    $[26] = waveId;
    $[27] = waveMessages;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const {
    serialTarget,
    queueSerialTarget,
    targetDropRef,
    isScrolling,
    scrollBaselineSerials,
    frozenAutoCollapseSerials
  } = useWaveDropsSerialScroll(t9);
  let t10;
  if ($[29] !== frozenAutoCollapseSerials || $[30] !== isScrolling || $[31] !== renderedWaveMessages?.drops || $[32] !== scrollBaselineSerials) {
    t10 = () => {
      if (!isScrolling || !scrollBaselineSerials) {
        return frozenAutoCollapseSerials;
      }
      const drops = renderedWaveMessages?.drops;
      if (!drops || drops.length === 0) {
        return frozenAutoCollapseSerials;
      }
      const next = new Set(frozenAutoCollapseSerials);
      for (const drop of drops) {
        const serialNo = drop.serial_no;
        if (typeof serialNo !== "number") {
          continue;
        }
        if (!scrollBaselineSerials.has(serialNo) && !next.has(serialNo)) {
          next.add(serialNo);
        }
      }
      return next;
    };
    $[29] = frozenAutoCollapseSerials;
    $[30] = isScrolling;
    $[31] = renderedWaveMessages?.drops;
    $[32] = scrollBaselineSerials;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  renderedWaveMessages?.drops;
  let t11;
  if ($[34] !== t10) {
    t11 = t10();
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const autoCollapseSerials = t11;
  const waveChatScroll = useWaveChatScrollOptional();
  let t12;
  let t13;
  if ($[36] !== queueSerialTarget || $[37] !== waveChatScroll || $[38] !== waveId) {
    t12 = () => {
      if (!waveChatScroll) {
        return;
      }
      return waveChatScroll.registerScrollHandler({
        waveId,
        handler: queueSerialTarget
      });
    };
    t13 = [waveChatScroll, waveId, queueSerialTarget];
    $[36] = queueSerialTarget;
    $[37] = waveChatScroll;
    $[38] = waveId;
    $[39] = t12;
    $[40] = t13;
  } else {
    t12 = $[39];
    t13 = $[40];
  }
  useEffect(t12, t13);
  let t14;
  if ($[41] !== forcePinToBottom || $[42] !== revealDeferredPendingDrops || $[43] !== waveMessages?.drops?.length) {
    t14 = () => {
      if (!waveMessages?.drops?.length) {
        return;
      }
      revealDeferredPendingDrops();
      forcePinToBottom();
    };
    $[41] = forcePinToBottom;
    $[42] = revealDeferredPendingDrops;
    $[43] = waveMessages?.drops?.length;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const revealPendingDrops = t14;
  let t15;
  if ($[45] !== dropId || $[46] !== fetchNextPage || $[47] !== waveId || $[48] !== waveMessages?.hasNextPage || $[49] !== waveMessages?.isLoading || $[50] !== waveMessages?.isLoadingNextPage) {
    t15 = async () => {
      if (waveMessages?.hasNextPage && !waveMessages?.isLoading && !waveMessages?.isLoadingNextPage) {
        await fetchNextPage({
          waveId,
          type: DropSize.FULL
        }, dropId);
      }
    };
    $[45] = dropId;
    $[46] = fetchNextPage;
    $[47] = waveId;
    $[48] = waveMessages?.hasNextPage;
    $[49] = waveMessages?.isLoading;
    $[50] = waveMessages?.isLoadingNextPage;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  waveMessages?.hasNextPage;
  waveMessages?.isLoading;
  waveMessages?.isLoadingNextPage;
  const handleTopIntersection = t15;
  let t16;
  if ($[52] !== queueSerialTarget || $[53] !== router || $[54] !== waveId) {
    t16 = drop_0 => {
      if (drop_0.wave.id === waveId) {
        queueSerialTarget(drop_0.serial_no);
      } else {
        const waveDetails = drop_0.wave as unknown as {
          chat?: {
            scope?: {
              group?: {
                is_direct_message?: boolean | undefined;
              } | undefined;
            } | undefined;
          } | undefined;
        } ?? undefined;
        const isDirectMessage = isWaveDirectMessage(drop_0.wave.id, waveDetails);
        const href = getWaveRoute({
          waveId: drop_0.wave.id,
          serialNo: drop_0.serial_no,
          isDirectMessage,
          isApp: false
        });
        router.push(href);
      }
    };
    $[52] = queueSerialTarget;
    $[53] = router;
    $[54] = waveId;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const handleQuoteClick = t16;
  const t17 = isScrolling || serialTarget !== null;
  let t18;
  if ($[56] !== activeDrop || $[57] !== autoCollapseSerials || $[58] !== boostedDrops || $[59] !== bottomAnchorCallbackRef || $[60] !== bottomAnchorRef || $[61] !== bottomPaddingClassName || $[62] !== dropId || $[63] !== forcePinToBottom || $[64] !== handleQuoteClick || $[65] !== handleTopIntersection || $[66] !== isAtBottom || $[67] !== onDropContentClick || $[68] !== onReply || $[69] !== pendingDropsCount || $[70] !== queueSerialTarget || $[71] !== renderedWaveMessages || $[72] !== revealPendingDrops || $[73] !== scrollContainerCallbackRef || $[74] !== scrollContainerRef || $[75] !== serialTarget || $[76] !== t17 || $[77] !== targetDropRef || $[78] !== typingMessage || $[79] !== unreadCount) {
    t18 = <TweetPreviewModeProvider mode="never"><WaveDropsContent waveMessages={renderedWaveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={handleTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={handleQuoteClick} isAtBottom={isAtBottom} scrollToBottom={forcePinToBottom} typingMessage={typingMessage} onDropContentClick={onDropContentClick} pendingCount={pendingDropsCount} onRevealPending={revealPendingDrops} bottomPaddingClassName={bottomPaddingClassName} boostedDrops={boostedDrops} onBoostedDropClick={queueSerialTarget} onScrollToUnread={queueSerialTarget} unreadCount={unreadCount} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={t17} /></TweetPreviewModeProvider>;
    $[56] = activeDrop;
    $[57] = autoCollapseSerials;
    $[58] = boostedDrops;
    $[59] = bottomAnchorCallbackRef;
    $[60] = bottomAnchorRef;
    $[61] = bottomPaddingClassName;
    $[62] = dropId;
    $[63] = forcePinToBottom;
    $[64] = handleQuoteClick;
    $[65] = handleTopIntersection;
    $[66] = isAtBottom;
    $[67] = onDropContentClick;
    $[68] = onReply;
    $[69] = pendingDropsCount;
    $[70] = queueSerialTarget;
    $[71] = renderedWaveMessages;
    $[72] = revealPendingDrops;
    $[73] = scrollContainerCallbackRef;
    $[74] = scrollContainerRef;
    $[75] = serialTarget;
    $[76] = t17;
    $[77] = targetDropRef;
    $[78] = typingMessage;
    $[79] = unreadCount;
    $[80] = t18;
  } else {
    t18 = $[80];
  }
  let t19;
  if ($[81] !== isScrolling) {
    t19 = <WaveDropsScrollingOverlay isVisible={isScrolling} />;
    $[81] = isScrolling;
    $[82] = t19;
  } else {
    t19 = $[82];
  }
  let t20;
  if ($[83] !== t18 || $[84] !== t19) {
    t20 = <div ref={containerRef} className="tw-relative tw-flex tw-h-full tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-iron-950">{t18}{t19}</div>;
    $[83] = t18;
    $[84] = t19;
    $[85] = t20;
  } else {
    t20 = $[85];
  }
  return t20;
};
export const WaveDropsAllWithoutProvider: React.FC<WaveDropsAllProps> = WaveDropsAllInner;
const WaveDropsAll: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(15);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const t2 = dividerSerialNo ?? null;
  const t3 = `unread-divider-${waveId}`;
  let t4;
  if ($[0] !== activeDrop || $[1] !== bottomPaddingClassName || $[2] !== dividerSerialNo || $[3] !== dropId || $[4] !== initialDrop || $[5] !== isMuted || $[6] !== onDropContentClick || $[7] !== onReply || $[8] !== unreadCount || $[9] !== waveId) {
    t4 = <WaveDropsAllInner key={waveId} waveId={waveId} dropId={dropId} onReply={onReply} activeDrop={activeDrop} initialDrop={initialDrop} dividerSerialNo={dividerSerialNo} unreadCount={unreadCount} onDropContentClick={onDropContentClick} bottomPaddingClassName={bottomPaddingClassName} isMuted={isMuted} />;
    $[0] = activeDrop;
    $[1] = bottomPaddingClassName;
    $[2] = dividerSerialNo;
    $[3] = dropId;
    $[4] = initialDrop;
    $[5] = isMuted;
    $[6] = onDropContentClick;
    $[7] = onReply;
    $[8] = unreadCount;
    $[9] = waveId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <UnreadDividerProvider initialSerialNo={t2} key={t3}>{t4}</UnreadDividerProvider>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
export default WaveDropsAll;